<template>
	<div class="all-notifications" :style="style" @pointerdown="onMouseDown" @pointerup="onMouseUp" @pointermove="onMouseMove" @pointercancel="onMouseUp" @lostpointercapture="onLostCapture" ref="an">
		<div v-if="slider" class="an-slider">
			<span class="an-slider-delay">{{label}}</span>
			<span class="an-slider-thumb" :style="thumbStyle"></span>
		</div>
		<template v-else><sym-icon name="replan"></sym-icon> {{ env.msg('butt.remindAllLater') }}</template>
	</div>
</template>

<script setup lang="ts">
import SymIcon from "@fd/helpers/SymIcon.vue";
import {injectEnv} from "@fd/Environment.ts";
import {computed, CSSProperties, ref} from "vue";
import {getElementMeasure} from "@fd/helpers/DomUtils.ts";

const env = injectEnv();
const emit = defineEmits(["postpone"]);

interface DelaySlot {
	perc:number;
	label:string;
	delay:number;
}
const delays:DelaySlot[] = [
	{ perc:0, label: env.msg('ring.minute.many', 5), delay: 5 },
	{ perc:0, label: env.msg('ring.minute.many', 10), delay: 10 },
	{ perc:0, label: env.msg('ring.minute.many', 15), delay: 15 },
	{ perc:0, label: env.msg('ring.minute.many', 20), delay: 20 },
	{ perc:0, label: env.msg('ring.minute.many', 30), delay: 30 },
	{ perc:0, label: env.msg('ring.minute.many', 45), delay: 45 },
	{ perc:0, label: env.msg('ring.hour.one'), delay: 60 },
	{ perc:0, label: env.msg('ring.hour.few', 2), delay: 120 },
	{ perc:0, label: env.msg('ring.hour.few', 4), delay: 240 },
	{ perc:0, label: env.msg('sdate.dayP1'), delay: minutesTomorrow() },
	{ perc:0, label: env.msg('sdate.dayP2'), delay: minutesTomorrow()+(24*60) }
];
const DEFAULT_SLOT = 6;

const slotCount = delays.length;
const slotStep = 1 / slotCount;
for (let i = 0; i < slotCount; i++) {
	delays[i].perc =  i * slotStep;
}

const HEIGHT = 28;

const slider = ref<boolean>(false);
const an = ref<HTMLDivElement | null>(null);
const buttonWidth = ref<number>(0);
const buttonHeight = ref<number>(0);
const viewPortX = ref<number>(0);
const startClientX = ref<number>(0);

const dragging = ref<boolean>(false);
const pos = ref<number>(0);
const percent = computed<number>(() => {
	if (!dragging.value) return delays[DEFAULT_SLOT].perc;
	if (buttonWidth.value > 0) {
		let p = pos.value - (HEIGHT / 2);
		return Math.max(Math.min(p / (buttonWidth.value - HEIGHT), 1), 0);
	} else return 0.5;
});
const delay = computed<DelaySlot>(() => {
	if (!dragging.value) return delays[DEFAULT_SLOT];
	for (let i = 1; i < delays.length; i++) {
		if (percent.value < delays[i].perc) return delays[i-1];
	}
	return delays[delays.length - 1];

});
const label = computed<string>(() => {
	return delay.value.label;
});

const style = computed<CSSProperties>(() => {
	if (slider.value) {
		const s: CSSProperties = {};
		s.backgroundColor = "var(--green)";
		s.width = buttonWidth.value + "px";
		s.height = buttonHeight.value + "px";
		s.padding = "0px";
		return s;
	} else return null;
});

const thumbStyle = computed<CSSProperties>(() => {
	const trackWidth = buttonWidth.value - HEIGHT;
	let left = trackWidth * percent.value + 2;
	return { left: left+"px" };
});

function onMouseDown(e:PointerEvent):void {
	if (an.value) {
		const m = getElementMeasure(an.value);
		buttonWidth.value = m.width;
		buttonHeight.value = m.height;
		viewPortX.value = m.viewLeft;
		startClientX.value = e.clientX;
		slider.value = true;
		an.value.setPointerCapture(e.pointerId);
	}
}

function onMouseMove(e:PointerEvent) {
	if (slider.value) {
		if (dragging.value) pos.value = e.clientX - viewPortX.value;
		else {
			if (Math.abs(e.clientX - startClientX.value) > 8) dragging.value = true;
		}
	}
}

function onMouseUp(e:PointerEvent):void {
	const currentSlot = delay.value;
	slider.value = false;
	dragging.value = false;
	if (an.value?.hasPointerCapture(e.pointerId)) an.value.releasePointerCapture(e.pointerId);
	emit("postpone", currentSlot.delay);
}

// Authoritative "gesture ended" signal - fires whenever capture is lost, even
// when a spurious capture drop means pointerup never reaches us.
function onLostCapture():void {
	slider.value = false;
	dragging.value = false;
}

// minutes until tomorrow 8am
function minutesTomorrow():number {
	const now = new Date().getTime();
	const t = new Date();
	t.setDate(t.getDate() + 1);
	t.setHours(8, 0, 0, 0);
	return Math.round((t.getTime() - now) / 60000);
}
</script>

<style>
.all-notifications {
	--an-height: 28px;
	padding: 0 16px;
	height: var(--an-height);
	line-height: var(--an-height);
	border-radius: calc(var(--an-height) / 2);
	font-size: 12px;
	max-width: none;
	background-color: var(--button-ternary-bg);
	color: var(--button-ternary-fg);
	align-self: flex-start;
	cursor: pointer;
	box-shadow: var(--panel-shadow);
	pointer-events: auto;
	display: flex;
	flex-direction: row;
	align-items: center;
	column-gap: 4px;
	user-select: none;
	-webkit-user-select: none;
}
.all-notifications:hover {
	background-color: var(--button-ternary-bg-hover);
	color: var(--button-ternary-fg-hover);
}
.all-notifications > .fi {
	font-size: 14px;
}
.an-slider {
	width: 100%;
	height: 100%;
	position: relative;

	& > .an-slider-delay {
		color: white;
		padding-inline: 16px;
		text-shadow: 0 0 3px #00000080;
	}
	& > .an-slider-thumb {
		position: absolute;
		display: inline-block;
		background-color: white;
		left: 2px;
		top: 2px;
		width: calc(var(--an-height) - 4px);
		height: calc(var(--an-height) - 4px);
		border-radius: calc((var(--an-height) - 4px) / 2);
		opacity: 0.5;
	}
}
</style>